import { RAIL_TYPE } from './railType';

export * from './dinValue';
import type { RailTypeKey } from './railType';

/**
 * Wire contract of the RCU4 firmware, taken from the source-derived
 * "RCU HTTP and MQTT command reference" (branch 4.2.0, commit 3986962).
 *
 * Two transports carry the same JSON:
 *
 *   POST http://<ip>:8900/command   {"type":"rcucmd","data":{…}}
 *   MQTT   <serial>/toDeviceRCU     the identical document
 *
 * and two carry the answers:
 *
 *   <serial>/toCloudRCU             acks, mapping reads, retained status
 *   UDP broadcast 255.255.255.255:56000
 *
 * The HTTP reply is only transport acceptance — always `{"status":"ok"}` for
 * non-OTA commands, even when parsing or execution failed — so nothing in this
 * app treats it as a result. Results arrive on the MQTT topics, which is why the
 * technician console needs a broker and the guest path cannot confirm anything.
 *
 * Two firmware limits shape what may be sent: a command body must stay under
 * 511 bytes (the handler's 512-byte buffer is sized from the remaining content
 * length, not from the space left), and the HTTP server accepts five sockets.
 */

/**
 * What the guest UI can ask a wired load to do. Each maps onto one firmware
 * command; `SET_TRAVEL` exists because a motor module has no absolute position.
 */
export const RCU_COMMAND = {
  SET_POWER: 'set_power',
  SET_LEVEL: 'set_level',
  SET_TRAVEL: 'set_travel',
} as const;

export type RcuCommandKind = (typeof RCU_COMMAND)[keyof typeof RCU_COMMAND];

export const RCU_PROTOCOL = {
  HTTP: 'http',
  HTTPS: 'https',
  /** Used until the technician picks the other one explicitly. */
  DEFAULT: 'http',
} as const;

export type RcuProtocol = (typeof RCU_PROTOCOL)[keyof typeof RCU_PROTOCOL];

/** The only two paths the firmware registers; anything else is a 404. */
export const RCU_API = {
  COMMAND_PATH: '/command',
  STATUS_PATH: '/status',
} as const;

/** Fixed TCP port of the RCU4 HTTP server (`0x22C4`). */
export const RCU_HTTP_PORT = 8900;

/** What `POST /command` answers, and what it means. */
export const RCU_HTTP_REPLY = {
  /** Acceptance only: the parser ran, not that the command worked. */
  OK: 'ok',
  /** OTA is the one command that reports its result in the HTTP reply. */
  OTA_SUCCESS: 'success',
  OTA_FAILED: 'failed',
} as const;

export const RCU_HTTP_LIMITS = {
  /** Keep command JSON under this plus the NUL the handler allocates. */
  MAX_COMMAND_BYTES: 511,
  MAX_OPEN_SOCKETS: 5,
  /** The `/status` document is built into this and errors if it does not fit. */
  STATUS_BUFFER_BYTES: 1_024,
} as const;

/** Outer discriminators: `rcucmd` plus the seven mapping commands. */
export const RCU_TOP_TYPE = {
  COMMAND: 'rcucmd',
  ADD_MAPPING: 'rcuaddmapping',
  READ_MAPPING: 'getrcumapping',
  ERASE_MAPPING: 'erasercumapping',
  ERASE_ALL_MAPPINGS: 'eraseallrcumapping',
  ADD_LED_MAPPING: 'rcuaddledmapping',
  READ_LED_MAPPING: 'getrculedmapping',
  ERASE_LED_MAPPING: 'eraserculedmapping',
} as const;

export type RcuTopType = (typeof RCU_TOP_TYPE)[keyof typeof RCU_TOP_TYPE];

/** Ack envelope discriminator. */
export const RCU_ACK_TYPE = 'rcuack';

/**
 * Top-level types the RCU sends that are not acks: they are complete documents,
 * raw rather than wrapped, and are also broadcast unchanged over UDP.
 */
export const RCU_INBOUND_TYPE = {
  /** The status document, retained on MQTT and served by `GET /status`. */
  STATUS: 'status',
  /** Scene mappings, from `getrcumapping`. */
  MAPPING_STATUS: 'rcumappingstatus',
  /** LED acknowledgement mappings, from `getrculedmapping`. */
  LED_MAPPING_STATUS: 'rculedmappingstatus',
  /** One coredump chunk, MQTT-only on the coredump topic. */
  COREDUMP_CHUNK: 'rcucoredump',
  /** A keypad scene's raw UART frame, MQTT-only at QoS 0. */
  RX_DATA: 'rxdata',
} as const;

/** `data.cmdtype` of every dispatchable command, in the reference's order. */
export const RCU_CMDTYPE = {
  OPERATE_RELAY: 'rcuoperate',
  READ_RELAY: 'getrcurelay',
  READ_ALL_RELAYS: 'getallrcurelays',
  OPERATE_DIN: 'dinoperate',
  READ_DIN: 'getdinstatus',
  READ_ALL_DIN: 'getdinstatusall',
  OPERATE_DALI: 'dalioperate',
  READ_DALI: 'getdalistatus',
  READ_STATUS: 'getrcustatus',
  LISTEN_PANEL: 'listenpanel',
  START_OTA: 'startota',
  SET_LOGGING: 'setlogging',
  READ_LOGGING_STATUS: 'getloggingstatus',
  READ_LOGS: 'getlogs',
  READ_COREDUMP_INFO: 'getcoredumpinfo',
  DOWNLOAD_COREDUMP: 'getcoredump',
  ERASE_COREDUMP: 'erasecoredump',
  IMPORT_S3_MAPPING: 'downloads3mapping',
} as const;

export type RcuCmdType = (typeof RCU_CMDTYPE)[keyof typeof RCU_CMDTYPE];

/**
 * Onboard relays are addressed as (group, relayno). Six groups of 26 exist and
 * ordinary RCU use is group 3, which is what this app sends; the console can
 * reach the other groups for commissioning.
 */
export const RCU_GROUP = {
  FIRST: 1,
  DEFAULT: 3,
  MIN: 1,
  MAX: 6,
} as const;

/** Relay count on one RCU, and the relay numbers the firmware understands. */
/** 1–6: the groups the firmware exposes, as the group picker lists them. */
export const RCU_GROUPS = [1, 2, 3, 4, 5, 6] as const satisfies readonly number[];

export const RELAY_NUMBERING = {
  FIRST: 1,
  LAST: 26,
} as const;

/**
 * `value` in `rcuoperate`. Only 0 and 1 persist; 2 reads the persisted state and
 * inverts it, and 4 rings a bell (on, then off after 500 ms). The firmware also
 * names 5 as an on-with-off-timer command, but the timer is not implemented, so
 * this app never sends it.
 */
export const DIN_MODULE = {
  RELAY: 0x02,
  ONE_TEN_V: 0x04,
  PHASE_CUT: 0x06,
  FAN: 0x08,
  CURTAIN: 0x0a,
  DALI_BRIDGE: 0x0c,
} as const;

export type DinModuleId = (typeof DIN_MODULE)[keyof typeof DIN_MODULE];

/** Base module codes are written in: two hex digits, upper case. */
export const MODULE_CODE_RADIX = 16;

/** `0x06` -> `"06"`, the spelling a request uses. */
export const dintypeHex = (module: DinModuleId): string =>
  module.toString(MODULE_CODE_RADIX).padStart(2, '0').toUpperCase();

/** `0x5a` -> `0x0a`: responses carry the full wire type, requests the low id. */
export const moduleIdFromWireType = (wireType: number): number => wireType & 0x0f;

/** Wire module id each rail type is driven as, the inverse of `dintypeHex`. */
export const RAIL_MODULE = {
  [RAIL_TYPE.RELAY]: DIN_MODULE.RELAY,
  [RAIL_TYPE.PHASE_CUT]: DIN_MODULE.PHASE_CUT,
  [RAIL_TYPE.ONE_TEN_V]: DIN_MODULE.ONE_TEN_V,
  [RAIL_TYPE.FAN]: DIN_MODULE.FAN,
  [RAIL_TYPE.CURTAIN]: DIN_MODULE.CURTAIN,
} as const satisfies Record<RailTypeKey, DinModuleId>;

export const moduleIdOfRail = (type: RailTypeKey): DinModuleId => RAIL_MODULE[type];

/** Firmware module each rail type is driven as. */
export const DIN_FIRMWARE_TYPE = {
  [RAIL_TYPE.RELAY]: dintypeHex(DIN_MODULE.RELAY),
  [RAIL_TYPE.PHASE_CUT]: dintypeHex(DIN_MODULE.PHASE_CUT),
  [RAIL_TYPE.ONE_TEN_V]: dintypeHex(DIN_MODULE.ONE_TEN_V),
  [RAIL_TYPE.FAN]: dintypeHex(DIN_MODULE.FAN),
  [RAIL_TYPE.CURTAIN]: dintypeHex(DIN_MODULE.CURTAIN),
} as const satisfies Record<RailTypeKey, string>;

/** `startota` is substituted into this compiled URL; there is no URL field. */
export const RCU_OTA = {
  BASE_URL: 'https://toyama-wifimesh-firmware.s3.ap-south-1.amazonaws.com/RCU/',
  PREFIX: 'RCU',
  SUFFIX: '.bin',
  /** Progress is reported at new multiples of this, so 100 is not guaranteed. */
  PROGRESS_STEP: 10,
  /** The progress value that means "done", which a report may never reach. */
  FULL_PERCENT: 100,
} as const;

/** Panel listening is enabled for this long, then times out. */
export const RCU_PANEL_LISTEN_SECONDS = 300;

/** Seconds in a minute, for reading a watch window aloud. */
export const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a second, for turning that window into a clock time. */
export const MS_PER_SECOND = 1_000;

/** Largest mapping import, in bytes. */
export const RCU_S3_IMPORT_MAX_BYTES = 32_767;

/**
 * mDNS service the firmware advertises from `toyama_mdns_init`: instance
 * "Toyama RCU", port 8900, and a hostname built from the serial number, so
 * `rcu-888854.local` belongs to the RCU whose serial is `888854`.
 */
export const RCU_DISCOVERY = {
  SERVICE_TYPE: '_toyama._tcp',
  SERVICE_INSTANCE: 'Toyama RCU',
  HOST_PREFIX: 'rcu-',
  HOST_SUFFIX: '.local',
  /** Longest serial the firmware hostname is assumed to carry. */
  MAX_SERIAL_LENGTH: 32,
  /** Serial as it appears in the hostname: letters, digits and dashes. */
  SERIAL_PATTERN: /^[A-Za-z0-9-]+$/,
} as const;
